import React, { useEffect, useRef } from 'react';
import { exportLayerOriginInfo, HOTKEY_TYPE, updateLayerPosition, useFramework } from '@/framework';
import { Layer } from '@/layers/layer';
import { TextLayer } from '@/layers/text-layer';
import { type QuickStageOperate } from '@/plugins/stage-operate';
import { type TextEditPlugin } from '@/plugins/text-edit/text-edit';
import { BaseLayerViewType, IExportPlugin, KEYBOARD_OPERATE_CLASSNAME, PluginType } from '@/version-types';

const HotkeyEvent: React.FC = React.memo(() => {
    const framework = useFramework();
    const timeoutHandle = useRef<NodeJS.Timeout>();
    useEffect(() => {
        const controller = new Layer(framework.identity);
        // 创建副本
        const cloneHandle = framework.events.on(HOTKEY_TYPE.createCopy, () => {
            const activeInfo = framework.operate.getActiveInfo();
            const layerId = activeInfo?.info.pickId || activeInfo?.info.id;
            if (layerId) {
                controller.resetPreAddLayerId(layerId);
                controller.clone(layerId);
            }
        });
        // 删除
        const deleteHandle = framework.events.onDelete(() => {
            const activeInfo = framework.operate.getActiveInfo();
            const layerId = activeInfo?.info.pickId || activeInfo?.info.id;
            if (layerId) {
                controller.delete(layerId);
            }
        });
        // 锁定
        const lockHandle = framework.events.on(HOTKEY_TYPE.lock, () => {
            const activeLayer = framework.operate.getActiveLayer();
            if (!activeLayer) return;
            if (activeLayer.lock) {
                controller.unlock(activeLayer.id);
            } else {
                controller.lock(activeLayer.id);
            }
        });
        // 统一预处理鼠标点击
        const mouseDownHandle = framework.events.onMouseDown(() => {
            document.body.classList.remove(KEYBOARD_OPERATE_CLASSNAME);
            const isDisablePick = framework.operate.isDisablePick();
            if (isDisablePick) return true;
            const isDisableOperate = framework.operate.isDisableOperate();
            if (isDisableOperate) return true;
        }, 8);
        // 统一预处理鼠标点击
        const mouseUpHandle = framework.events.onMouseUp(() => {
            const isDisablePick = framework.operate.isDisablePick();
            if (isDisablePick) return true;
            const isDisableOperate = framework.operate.isDisableOperate();
            if (isDisableOperate) return true;
        }, 8);
        return () => {
            framework.events.off(deleteHandle);
            framework.events.off(lockHandle);
            framework.events.off(mouseDownHandle);
            framework.events.off(mouseUpHandle);
            framework.events.off(cloneHandle);
        };
    }, []);

    // 方向快捷键
    useEffect(() => {
        const setSmoothlyMoveDuration = (duration: string) => {
            document.documentElement.style.setProperty('--smoothly-move-duration', duration);
        };
        // 移动图层
        const moveLayer = (x: number, y: number) => {
            const activeLayer = framework.operate.getActivePickLayer();
            if (framework.operate.isDisableMove() || !activeLayer) return false;
            if (activeLayer.lock) return true;
            setSmoothlyMoveDuration('0.2s');
            const activeNode = document.querySelector('.active-operate');
            if (activeNode) {
                document.body.classList.add(KEYBOARD_OPERATE_CLASSNAME);
            }
            const layerId = activeLayer.id;
            const oldLayer = framework.getLayer(layerId, true);
            framework.events.emitBeforeBaseOperate(oldLayer, BaseLayerViewType.MOVE);
            const originLayerInfo = exportLayerOriginInfo(framework.identity, layerId);
            const { originInfo, childOriginInfoMap } = originLayerInfo;
            updateLayerPosition(framework.identity, layerId, x, y, originInfo, childOriginInfoMap, {
                isStart: false,
                isEnd: false,
            });
            clearTimeout(timeoutHandle.current);
            timeoutHandle.current = setTimeout(() => {
                setSmoothlyMoveDuration('0s');
                updateLayerPosition(framework.identity, layerId, x, y, originInfo, childOriginInfoMap, {
                    isStart: false,
                    isEnd: true,
                });
                document.body.classList.remove(KEYBOARD_OPERATE_CLASSNAME);
                framework.stage.addOrUpdate(framework.getLayer(layerId, true));
                framework.history.write(oldLayer);
                framework.operate.setActiveLayer(layerId);
            }, 500);
            return true;
        };
        // 移动舞台
        const changeStage = (isDown: boolean) => {
            if (isDown) {
                framework.getInstance<QuickStageOperate>('QuickStageOperate')?.down();
            } else {
                framework.getInstance<QuickStageOperate>('QuickStageOperate')?.up();
            }
        };
        const moveDistance = 1;
        const quickMoveDistance = 10;
        const up = framework.events.on(HOTKEY_TYPE.up, () => {
            if (!moveLayer(0, -moveDistance)) {
                changeStage(false);
            }
        });
        const right = framework.events.on(HOTKEY_TYPE.right, () => {
            if (!moveLayer(moveDistance, 0)) {
                changeStage(true);
            }
        });
        const down = framework.events.on(HOTKEY_TYPE.down, () => {
            if (!moveLayer(0, moveDistance)) {
                changeStage(true);
            }
        });
        const left = framework.events.on(HOTKEY_TYPE.left, () => {
            if (!moveLayer(-moveDistance, 0)) {
                changeStage(false);
            }
        });
        const quickUp = framework.events.on(HOTKEY_TYPE.quickUp, () => {
            if (!moveLayer(0, -quickMoveDistance)) {
                changeStage(false);
            }
        });
        const quickRight = framework.events.on(HOTKEY_TYPE.quickRight, () => {
            if (!moveLayer(quickMoveDistance, 0)) {
                changeStage(true);
            }
        });
        const quickDown = framework.events.on(HOTKEY_TYPE.quickDown, () => {
            if (!moveLayer(0, quickMoveDistance)) {
                changeStage(true);
            }
        });
        const quickLeft = framework.events.on(HOTKEY_TYPE.quickLeft, () => {
            if (!moveLayer(-quickMoveDistance, 0)) {
                changeStage(false);
            }
        });
        return () => {
            framework.events.off(up);
            framework.events.off(right);
            framework.events.off(down);
            framework.events.off(left);
            framework.events.off(quickUp);
            framework.events.off(quickRight);
            framework.events.off(quickDown);
            framework.events.off(quickLeft);
        };
    }, []);

    // 排序快捷键
    useEffect(() => {
        const controller = new Layer(framework.identity);
        const getActiveLayerSortInfo = () => {
            const activeLayer = framework.operate.getActiveLayer();
            if (!activeLayer) return;
            return controller.getSortData(activeLayer.id);
        };
        /** 上移一层 */
        const handler1 = framework.events.on(HOTKEY_TYPE.sortUp, () => {
            const res = getActiveLayerSortInfo();
            if (res && !res.operate.disableUp) {
                controller.sort(1, res.data);
            }
        });
        /** 下移一层 */
        const handler2 = framework.events.on(HOTKEY_TYPE.sortDown, () => {
            const res = getActiveLayerSortInfo();
            if (res && !res.operate.disableDown) {
                controller.sort(-1, res.data);
            }
        });
        /** 置顶 */
        const handler3 = framework.events.on(HOTKEY_TYPE.sortTop, () => {
            const res = getActiveLayerSortInfo();
            if (res && !res.operate.disableUp) {
                controller.sort(res.cursor.upCount, res.data);
            }
        });
        /** 置底 */
        const handler4 = framework.events.on(HOTKEY_TYPE.sortBottom, () => {
            const res = getActiveLayerSortInfo();
            if (res && !res.operate.disableDown) {
                controller.sort(res.cursor.downCount, res.data);
            }
        });
        return () => {
            framework.events.off(handler1);
            framework.events.off(handler2);
            framework.events.off(handler3);
            framework.events.off(handler4);
        };
    }, []);

    // 通过快捷键添加图层
    useEffect(() => {
        const tryGetTextEditPlugin = () => {
            const textEdit = framework.getInstance<TextEditPlugin>('TextEditPlugin');
            if (!textEdit) {
                console.error('TextEditPlugin is not found');
                return null;
            }
            return textEdit;
        };
        // 添加默认文本图层
        const addTextLayerListener = framework.events.on(HOTKEY_TYPE.T, async () => {
            const layer = await framework.getInstance(TextLayer).addByDefaultContent();
            tryGetTextEditPlugin()?.enterEditing(layer.id);
        });

        return () => {
            framework.events.off(addTextLayerListener);
        };
    }, []);

    // 通过ESC取消选中的图层
    useEffect(() => {
        const escapeLayerActiveListener = framework.events.onEsc(() => {
            if (framework.operate.isBaseOperate()) return;
            framework.operate.delActiveLayer();
        });
        return () => {
            framework.events.off(escapeLayerActiveListener);
        };
    }, []);

    return null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.SYSTEM,
    sort: 1,
    name: 'hotkey-event',
    render() {
        return <HotkeyEvent />;
    },
};

export default exportPlugin;
